import type { ApiClient } from "@listto/client";
import { BasePowerSyncConnector, type CrudOperation, type TokenStorage } from "./base-connector";
import { HTTP_FORBIDDEN, HTTP_UNAUTHORIZED } from "../constants/common";

export interface ListtoConnectorConfig {
  /** PowerSync Instance URL */
  endpoint: string;
  /** Shared token storage implementation */
  storage: TokenStorage;
  /** Pre-configured OpenAPI client */
  apiClient: ApiClient;
  /** Callback when auth errors (401/403) occur to notify session machines */
  onAuthError?: (error: unknown) => void;
  /** Optional guard to skip network calls if we know we are visitors */
  shouldFetchToken?: () => boolean;
}

/**
 * Shared PowerSync Connector for the Listto ecosystem.
 * Consolidates common auth and batch upload logic used by both Web and Native apps.
 */
export class ListtoPowerSyncConnector extends BasePowerSyncConnector {
  constructor(listtoConfig: ListtoConnectorConfig) {
    super({
      endpoint: listtoConfig.endpoint,
      storage: listtoConfig.storage,
      fetchAuthToken: async () => {
        if (listtoConfig.shouldFetchToken && !listtoConfig.shouldFetchToken()) {
          return null;
        }

        try {
          console.log("[PowerSync] Fetching auth token...");
          const { data, error, response } = await listtoConfig.apiClient.GET(
            "/api/auth/get-session",
            {
              params: { query: { jwt: true } },
            },
          );

          const { ok, status } = response;

          if (!ok || !data) {
            if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
              console.warn("[PowerSync] Auth failed (401/403).");
              // Ensure the status is included so the session machine can recognize the auth failure
              const authError =
                error && typeof error === "object"
                  ? { ...error, status }
                  : { message: "Unauthorized", status };
              listtoConfig.onAuthError?.(authError);
              return null;
            }
            throw error instanceof Error
              ? error
              : new Error(JSON.stringify(error ?? "Unknown error"));
          }

          return data.token ?? null;
        } catch (err) {
          console.error("[PowerSync] Error in fetchAuthToken:", err);
          return null;
        }
      },
      uploadBatch: async (operations: CrudOperation[]) => {
        const result = await listtoConfig.apiClient.POST("/api/sync/batch", {
          body: { operations },
        });

        if (result.error) {
          throw result.error instanceof Error
            ? result.error
            : new Error(JSON.stringify(result.error));
        }
      },
    });
  }
}
